import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, FileText, Users, Shield, Calendar, Archive as ArchiveIcon, ChevronRight, ClipboardList } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [polls, setPolls] = useState([]);
  const [threads, setThreads] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [archive, setArchive] = useState([]);
  const [nextSession, setNextSession] = useState(null);
  const [pending, setPending] = useState(0);
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/polls").then(r=>setPolls(r.data)).catch(()=>{});
    api.get("/threads").then(r=>setThreads(r.data)).catch(()=>{});
    api.get("/portfolios").then(r=>setPortfolios(r.data)).catch(()=>{});
    api.get("/archive").then(r=>setArchive(r.data)).catch(()=>{});
    api.get("/sessions/next").then(r=>setNextSession(r.data && r.data.id ? r.data : null)).catch(()=>{});
    api.get("/surveys/pending/mine").then(r=>setPendingSurveys(r.data)).catch(()=>{});
    api.get("/hrm/applications/mine").then(r=>setMyApps(r.data)).catch(()=>{});
    if (user?.role === "band_registry_clerk" || user?.is_super_admin) {
      api.get("/registry/pending").then(r=>setPending(r.data.length)).catch(()=>{});
    }
    const t = setInterval(()=>setNow(Date.now()), 1000);
    return ()=>clearInterval(t);
  }, [user]);

  const official = polls.filter(p => p.poll_type === "official");
  const community = polls.filter(p => p.poll_type === "community");

  const canSeeClerk = user?.role === "band_registry_clerk" || user?.is_super_admin;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">Member dashboard</div>
          <h1 className="font-display text-4xl mt-2 leading-tight">Boozhoo, <span className="text-white/70">{user?.first_name}</span>.</h1>
          <div className="font-mono text-xs text-white/50 mt-2">
            {ROLE_LABEL[user?.role]}{user?.is_owner && <span className="text-[color:var(--cobalt)] ml-2">· OWNER</span>}{user?.is_super_admin && !user?.is_owner && <span className="text-[color:var(--cobalt)] ml-2">· SUPER ADMIN</span>} · Registry #{user?.band_registry_number}
          </div>
        </div>
      </div>

      {/* My Applications — surfaces when the member has any application on file */}
      {myApps.length > 0 && (
        <div className="mt-8 border border-white/10 bg-white/[0.02] p-5" data-testid="my-apps-widget">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="label-mono">Your applications · {myApps.length}</div>
            <Link to="/portal/careers/mine" className="font-mono text-[10px] text-[color:var(--cobalt)] hover:underline uppercase tracking-widest" data-testid="my-apps-view-all">View all →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {myApps.slice(0, 3).map(a => {
              const style = a.status === "selected" ? "#89b4fa"
                : a.status === "not_selected" || a.status === "withdrawn" ? "#7f849c"
                : a.status === "shortlisted" ? "#d4b876" : "#a6adc8";
              const label = a.status === "selected" ? "Selected — congratulations"
                : a.status === "shortlisted" ? "Shortlisted"
                : a.status === "not_selected" ? "Not selected"
                : a.status === "withdrawn" ? "Withdrawn"
                : "Received";
              return (
                <Link key={a.id} to="/portal/careers/mine" className="border border-white/8 bg-black/30 p-3 hover:border-[color:var(--cobalt)]/50">
                  <span className="tag" style={{color: style}}>{label}</span>
                  <div className="font-display mt-2">{a.posting_title}</div>
                  <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
                    submitted {new Date(a.submitted_at).toLocaleDateString()}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending consultation — only appears when the caller has a survey to respond to */}
      {pendingSurveys.length > 0 && (
        <div className="mt-8 border border-[color:var(--cobalt)]/50 bg-[color:var(--cobalt)]/[0.04] p-5" data-testid="pending-surveys-widget">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <span className="inline-block w-1.5 h-1.5 bg-[color:var(--cobalt)] animate-pulse"/>
              <span className="label-mono text-[color:var(--cobalt)]">Consultation awaiting your response · {pendingSurveys.length}</span>
            </div>
            <Link to="/portal/surveys" className="font-mono text-[10px] text-[var(--cobalt)] hover:underline uppercase tracking-widest" data-testid="pending-surveys-view-all">View all →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pendingSurveys.slice(0, 3).map(s => {
              const closesIn = s.closes_at ? Math.max(0, Math.round((new Date(s.closes_at) - now) / 86400000)) : null;
              const targetPct = s.response_target ? Math.round((s.response_count / s.response_target) * 100) : null;
              return (
                <Link
                  key={s.id}
                  to="/portal/surveys"
                  className="border border-white/15 bg-black/40 p-4 hover:border-[var(--cobalt)] transition-colors"
                  data-testid={`pending-survey-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 font-mono text-[9px] text-[var(--cobalt)] uppercase tracking-widest">
                      <ClipboardList size={11}/>
                      {s.question_count} {s.question_count === 1 ? "question" : "questions"}
                      {s.is_anonymous && <span className="text-[#89b4fa]">· anonymous</span>}
                    </div>
                    {closesIn !== null && (
                      <span className="font-mono text-[10px] tabular-nums" style={{color: closesIn <= 3 ? "#ef5b6b" : closesIn <= 7 ? "var(--cobalt)" : "rgba(255,255,255,0.5)"}}>
                        {closesIn}D LEFT
                      </span>
                    )}
                  </div>
                  <div className="font-display text-base leading-tight text-white">{s.title}</div>
                  <div className="font-mono text-[10px] text-white/50 mt-2">
                    {s.created_by_name}{s.created_by_role && ` · ${ROLE_LABEL[s.created_by_role] || s.created_by_role}`}
                  </div>
                  {targetPct !== null && (
                    <div className="mt-3">
                      <div className="flex justify-between font-mono text-[9px] text-white/40 uppercase tracking-widest">
                        <span>Turnout</span>
                        <span>{s.response_count}/{s.response_target}</span>
                      </div>
                      <div className="h-0.5 bg-white/10 mt-1"><div className="h-full bg-[var(--cobalt)]" style={{width: `${Math.min(100, targetPct)}%`}}/></div>
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-[var(--cobalt)] mt-3 uppercase tracking-widest">Respond →</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
        <StatCard icon={Radio} label="Official items" value={official.length} to="/portal/polls" testid="stat-official"/>
        <StatCard icon={Users} label="Community items" value={community.length} to="/portal/polls" testid="stat-community"/>
        <StatCard icon={ArchiveIcon} label="Archive records" value={archive.length} to="/portal/archive" testid="stat-archive"/>
        {canSeeClerk ? (
          <StatCard icon={Shield} accent="signal" label="Pending applicants" value={pending} to="/portal/clerk" testid="stat-pending"/>
        ) : (
          <StatCard icon={FileText} label="Portfolios" value={portfolios.length} to="/portal/portfolios" testid="stat-portfolios"/>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-10">
        {/* Next Session */}
        <button onClick={()=>nav("/portal/sessions")} className="panel p-6 corner-tick text-left" data-testid="dashboard-next-session">
          <div className="label-mono flex items-center gap-2"><Calendar size={12}/> Next council session</div>
          {nextSession ? (
            <>
              <div className="font-display text-lg mt-3">{nextSession.title}</div>
              <div className="font-mono text-[10px] text-white/50 mt-1">{new Date(nextSession.scheduled_at).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}</div>
              <Countdown target={new Date(nextSession.scheduled_at).getTime()} now={now}/>
            </>
          ) : (
            <div className="font-mono text-xs text-white/40 mt-4">None scheduled. Chief &amp; Council to publish.</div>
          )}
        </button>

        {/* Latest archive */}
        <div className="panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">Recent archive additions</div>
            <Link to="/portal/archive" className="font-mono text-[10px] text-white/50 hover:text-white uppercase tracking-widest">View all →</Link>
          </div>
          {archive.length === 0 ? (
            <div className="font-mono text-xs text-white/40">Nothing filed yet. Archive builds up as records are added.</div>
          ) : (
            <div className="space-y-2">
              {archive.slice(0, 3).map(a => (
                <Link key={a.id} to="/portal/archive" className="block border border-white/8 p-3 hover:border-[color:var(--cobalt)]/50">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-[color:var(--cobalt)] tracking-widest uppercase truncate">{a.category}{a.year?` · ${a.year}`:""}</span>
                  </div>
                  <div className="font-display text-sm mt-1 truncate">{a.title}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">Active official ballots</div>
            <Link to="/portal/polls" className="font-mono text-[10px] text-white/50 hover:text-white uppercase tracking-widest" data-testid="dashboard-polls-link">View all →</Link>
          </div>
          {official.length === 0 && <div className="font-mono text-xs text-white/40">No official items posted.</div>}
          <div className="space-y-3">
            {official.slice(0,3).map(p => (
              <Link key={p.id} to="/portal/polls" className="block border border-white/10 p-4 hover:border-[#89b4fa]/50">
                <div className="font-mono text-[10px] text-[var(--cobalt)] uppercase tracking-widest">{p.category} · {p.created_by_role.replace('_',' ')}</div>
                <div className="font-display text-lg mt-1">{p.title}</div>
                <div className="font-mono text-[10px] text-white/40 mt-1">{p.total_votes} votes · {p.has_voted ? "You voted" : "Awaiting your vote"}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">Recent discussions</div>
            <Link to="/portal/discussions" className="font-mono text-[10px] text-white/50 hover:text-white uppercase tracking-widest" data-testid="dashboard-threads-link">View all →</Link>
          </div>
          {threads.length === 0 ? (
            <div className="font-mono text-xs text-white/40">No community threads yet. Any member can start one.</div>
          ) : (
            <div className="space-y-3">
              {threads.slice(0,3).map(t => (
                <Link key={t.id} to="/portal/discussions" className="block border border-white/8 p-4 hover:border-[color:var(--cobalt)]/50">
                  <div className="font-display text-lg">{t.title}</div>
                  <div className="font-mono text-[10px] text-white/40 mt-1">{t.created_by_name} · {t.reply_count} replies</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, accent, label, value, to, testid }) {
  const color = accent === "signal" ? "var(--signal)" : "var(--cobalt)";
  return (
    <Link to={to} className="panel panel-lift p-5 corner-tick block" data-testid={testid}>
      <Icon size={18} style={{ color }} />
      <div className="font-display text-3xl mt-3">{value}</div>
      <div className="label-mono mt-1">{label}</div>
      <div className="flex items-center gap-1 mt-2 font-mono text-[10px] text-white/40 uppercase tracking-widest">Open <ChevronRight size={10}/></div>
    </Link>
  );
}

function Countdown({ target, now }) {
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="grid grid-cols-4 gap-2 mt-4 font-mono">
      {[[d,"D"],[h,"H"],[m,"M"],[s,"S"]].map(([v,l],i)=>(
        <div key={i} className="border border-white/8 p-2 text-center">
          <div className="text-lg text-[color:var(--cobalt)] tabular-nums">{String(v).padStart(2,"0")}</div>
          <div className="text-[9px] text-white/40 uppercase tracking-widest">{l}</div>
        </div>
      ))}
    </div>
  );
}
